// File: src/pages/seller/AddProduct/sections/ProductMedia.jsx
import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  CRow,
  CCol,
  CFormLabel,
  CFormInput,
  CButton,
  CSpinner,
  CFormText,
} from "@coreui/react";
import LiveVideoCapture from "../../../../components/seller/LiveVideoCapture.jsx";
import { FaMagic, FaCut, FaExclamationTriangle, FaVideo } from "react-icons/fa";

const ProductMedia = ({
  onAiEdit,
  aiLoading,
  uploadedImage,
  imagePreview,
  setImagePreview,
  setVideoPreview,
  videoPreview,
  kycType,
  isEditMode,
}) => {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const watchedImageFile = watch("image")?.[0];
  const aiPreviewSrc = watch("aiPreview");
  const [activeAiAction, setActiveAiAction] = useState(null);

  useEffect(() => {
    if (!aiLoading) {
      setActiveAiAction(null);
    }
  }, [aiLoading]);

  const handleAiEdit = (action) => {
    setActiveAiAction(action);
    onAiEdit(action);
  };

  useEffect(() => {
    console.log("👀 aiPreview value:", aiPreviewSrc);
  }, [aiPreviewSrc]);

  // Update parent preview when a new file is selected
  useEffect(() => {
    // Run ONLY when user selects a NEW image (not AI edit)
    if (watchedImageFile && !watch("aiPreview")) {
      const url = URL.createObjectURL(watchedImageFile);
      setImagePreview(url);
      setValue("existingImage", null);
    }
  }, [watchedImageFile, setImagePreview, setValue, watch]);

  // Initialize existing image
  useEffect(() => {
    if (imagePreview && !watchedImageFile) {
      setValue("existingImage", imagePreview);
    }
  }, [imagePreview, watchedImageFile, setValue]);

  return (
    <div
      style={{
        border: "1px solid #dcdcdc",
        borderRadius: "8px",
        padding: "1.5rem",
        position: "relative",
        marginBottom: "1.5rem",
      }}
    >
      {/* Section Title */}
      <span
        style={{
          position: "absolute",
          top: "-0.8rem",
          left: "1rem",
          backgroundColor: "white",
          padding: "0 0.5rem",
          fontWeight: "600",
          color: "#333",
          fontSize: "1rem",
        }}
      >
        Product Media
      </span>

      <CRow className="mb-3">
        <CCol md={12}>
          <CFormLabel>Product Image</CFormLabel>
          <CFormInput
            type="file"
            {...register("image")}
            accept=".jpg,.jpeg,.png"
          />

          {/* The Help Note */}
          <CFormText
            className="mt-2 px-3 py-2 d-block border-0"
            style={{
              backgroundColor: "#fff3cd",
              borderRadius: "8px",
              color: "#856404",
              borderLeft: "4px solid #ffc107",
            }}
          >
            <div className="d-flex align-items-center">
              <span className="me-2">⚠️</span>
              <small>
                <strong>Important:</strong> Only <strong>JPEG</strong>, <strong>JPG</strong> or{" "}
                <strong>PNG</strong> formats are supported. Keep file size under{" "}
                <strong>2MB</strong> to avoid upload errors.
              </small>
            </div>
          </CFormText>
          {/* Hidden field to let RHF know about existing image */}
          <input type="hidden" {...register("existingImage")} />
          {errors.image && (
            <div style={{ color: "red" }}>{errors.image.message}</div>
          )}
        </CCol>

        <CCol md={12}>
          <div className="mt-3">
            <div
              style={{
                display: "flex",
                gap: 16,
                overflowX: "auto",
                paddingBottom: "10px",
              }}
            >
              {/* Uploaded Image (Visible only in Edit mode) */}
              {isEditMode && uploadedImage && (
                <div style={{ flex: 1, minWidth: "200px" }}>
                  <CFormLabel>Uploaded Image</CFormLabel>
                  <div
                    style={{
                      width: "100%",
                      height: 240,
                      border: "2px solid #198754",
                      borderRadius: 8,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f8fffb",
                    }}
                  >
                    <img
                      src={uploadedImage}
                      alt="uploaded"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>
              )}

              {/* New Selection Preview */}
              <div style={{ flex: 1, minWidth: "200px" }}>
                <CFormLabel>
                  {isEditMode ? "New Selection" : "Original Preview"}
                </CFormLabel>
                <div
                  style={{
                    width: "100%",
                    height: 240,
                    border: "2px dashed #868e96",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <span style={{ color: "#868e96" }}>No image selected</span>
                  )}
                </div>
              </div>

              {/* AI Edited Preview */}
              {/* <div style={{ flex: 1, minWidth: "200px" }}>
                <CFormLabel>AI Edited Preview</CFormLabel>
                <div
                  style={{
                    width: "100%",
                    height: 240,
                    border: "2px dashed #868e96",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {aiPreviewSrc ? (
                    <img
                      src={aiPreviewSrc}
                      alt="ai"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <span style={{ color: "#868e96" }}>No AI edit yet</span>
                  )}
                </div>
              </div> */}
            </div>

            {/* Buttons below previews */}
            {/* <div className="d-flex gap-2 mt-3">
              <CButton
                color="secondary"
                variant="outline"
                size="sm"
                disabled={aiLoading && activeAiAction === "enhance"}
                onClick={() => handleAiEdit("enhance")}
              >
                <FaMagic className="me-1" />
                Enhance
                {aiLoading && activeAiAction === "enhance" && (
                  <CSpinner size="sm" className="ms-2" />
                )}
              </CButton>
              <CButton
                color="secondary"
                variant="outline"
                size="sm"
                disabled={aiLoading && activeAiAction === "remove_bg"}
                onClick={() => handleAiEdit("remove_bg")}
              >
                <FaCut className="me-1" />
                Remove BG
                {aiLoading && activeAiAction === "remove_bg" && (
                  <CSpinner size="sm" className="ms-2" />
                )}
              </CButton>
            </div> */}
          </div>
        </CCol>

        {/* Product Video Section */}
        <CCol md={12}>
          <div className="mt-4">
            <CFormLabel>Product Video (Live Verification)</CFormLabel>

            {/* CREATE MODE */}
            {isEditMode ? (
              videoPreview ? (
                <LiveVideoCapture defaultVideoUrl={videoPreview} readonly />
              ) : (
                <div
                  style={{
                    border: "2px dashed #ccc",
                    borderRadius: 8,
                    padding: "1rem",
                    textAlign: "center",
                    background: "#fafafa",
                    color: "#777",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                  }}
                >
                  <FaExclamationTriangle
                    style={{ color: "#777", fontSize: "1.2rem" }}
                  />
                  No verification video available for this product.
                </div>
              )
            ) : (
              <div
                style={{
                  border: "2px dashed #ccc",
                  borderRadius: 8,
                  padding: "1rem",
                  textAlign: "center",
                  background: "#fafafa",
                  color: "#555",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                <FaVideo style={{ color: "#555", fontSize: "1.2rem" }} />
                Once the listing is saved, a live video verification link will
                be sent to you on your registered mobile number and email. The
                link will be valid for 24 hours.
              </div>
            )}

            {kycType === "individual" && (
              <div
                style={{
                  color: "#d9534f",
                  fontSize: "0.9em",
                  marginTop: "0.5rem",
                }}
              >
                *Note: Video upload is <strong>mandatory</strong> for individual
                KYC users.
              </div>
            )}
          </div>
        </CCol>
      </CRow>
    </div>
  );
};

export default React.memo(ProductMedia);
